import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import CustomFieldVM, { CustomFieldType } from '../custom-field/custom-field.viewmodel';
import { alertServerError, showNotification } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { RegistrationConfigurationDAO } from '../../../../../../../api/implementations/registration-configuration.dao';
import { getFormattedUrl } from '../../../../../../../infrastructure/utils/common.utils';
import { ICustomFieldFormConfiguration, ICustomFieldsDTO } from '../../../../../../../api/custom-fields.dao';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import { CustomRadioValues } from '../custom-field/custom-field.presentation';
import { LinkTargetValue } from '../../../../../../../constants/common.constants';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

export default class FormRegistrationFormVM {

  @mobx.observable
  public customFieldView: CustomFieldVM;

  @mobx.observable
  public isSaving = false;

  @mobx.observable
  public isFormApplied: boolean;

  @mobx.observable
  public isLoading: boolean;

    private customFieldsTypeToTrack = {
        ['Custom Field - ' + CustomFieldType.text]: 0,
        ['Custom Field - ' + CustomFieldType.checkbox]: 0,
        ['Custom Field - ' + CustomFieldType.dropdown]: 0,
        ['Custom Field - ' + CustomFieldType.textarea]: 0
    };

    private customFieldsAddedTypeToTrack = {
        ['Custom Field Added - ' + CustomFieldType.text]: convertBoolTypeForMixPanel(false),
        ['Custom Field Added - ' + CustomFieldType.checkbox]: convertBoolTypeForMixPanel(false),
        ['Custom Field Added - ' + CustomFieldType.dropdown]: convertBoolTypeForMixPanel(false),
        ['Custom Field Added - ' + CustomFieldType.textarea]: convertBoolTypeForMixPanel(false)
    };

  constructor(
    private readonly accountId: number,
    private readonly eventId: number,
    private readonly subdomain: string,
    private readonly closeConfigureRegistrationForm: () => void,
    @IoC.Inject private readonly registrationConfigurationDAO: RegistrationConfigurationDAO = IoC.Container.get(RegistrationConfigurationDAO),
    @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
  ) {
    this.isLoading = true;
    this.accountId = accountId;
    this.eventId = eventId;
    this.customFieldView = new CustomFieldVM(
      this.deleteCustomField
    );
    this.customFieldView.closeConfigureRegistrationForm = this.closeConfigureRegistrationForm;
    this.isFormApplied = false;
  }

  @mobx.action
  public async initialize() {
    await this.getRegistrationConfiguration();
  }

  public getRegistrationConfiguration = async (): Promise<void> => {
    try {
      const result = await this.registrationConfigurationDAO.getFormRegistrationConfiguration(this.eventId, this.accountId);
      mobx.runInAction(() => {
        this.customFieldView.triagedRegistrationEnabled = result.triagedRegistrationEnabled;
        this.customFieldView.onFetchCustomFieldsListSuccess(result.fields.map(field => {
          return {
            ...field,
            label: transpileInlineStylesToClasses(field.label)
          };
        }));
      });
    } catch (e) {
      FormRegistrationFormVM.handleApiException(e);
    } finally {
        mobx.runInAction(() => {
            this.isLoading = false;
        });
    }
  };

  public static handleApiException(e: Error, title: string = 'Error'): void {
    if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
  }

  @mobx.action
  public applyRegistrationForm = async (): Promise<boolean> => {
    if (this.isSaving) return;

    try {
      mobx.runInAction(() => {
        this.isFormApplied = true;
        this.isSaving = true;
      });

      if (!this.customFieldView.checkValidity()) return false;
      this.closeConfigureRegistrationForm();

      const registrationConfiguration = {
        ...{ fields: this.customFieldView.customFieldsToDTO().map(field => ({
          ...field,
          ...(field.label && { label: transpileClassesToInlineStyles(field.label) })
        })) }
      };

      const response = await this.registrationConfigurationDAO.updateFormRegistrationConfiguration(this.accountId, this.eventId, registrationConfiguration);
      this.customFieldView.onFetchCustomFieldsListSuccess(response.fields);

      this.trackRegistrationFields(registrationConfiguration.fields);
      this.customFieldView.setInitialFields();
      this.setToDefaultCustomFieldsTypeToTrack();

      showNotification('Form updated');
      return true;
    }
    catch (e) {
      FormRegistrationFormVM.handleApiException(e);
      return false;
    } finally {
      mobx.runInAction(() => {
        this.isSaving = false;
      });
    }
  };

    private trackRegistrationFields = (registrationFields: ICustomFieldsDTO[]): void => {
        const { fields } = this.customFieldView;

        this.trackingService.get().track(
            TRACKING_EVENTS.CONFIGURE_REGISTRATION_FORM,
            {
                ...registrationFields.reduce((result, field) => {
                    if (!field.default) {
                        const isNewField = !fields.some(initialField => initialField.customFieldId === field.customFieldId);
                        const fieldConfig: ICustomFieldFormConfiguration = field.configurations?.[0] || null;
                        if (isNewField && fieldConfig) {
                            this.trackingService.get().track(TRACKING_EVENTS.CONFIGURE_REGISTRATION_FORM_CUSTOM_FIELD, {
                                'Field Type': field.type,
                                'Description': convertBoolTypeForMixPanel(Boolean(field.description)),
                                'Custom field Mandatoriness': fieldConfig.mandatory ? CustomRadioValues.required : CustomRadioValues.optional,
                                'Custom field displaying option': convertBoolTypeForMixPanel(fieldConfig.visible)
                            });
                            this.customFieldsAddedTypeToTrack['Custom Field Added - ' + field.type] = convertBoolTypeForMixPanel(true);
                        }
                        if (fieldConfig?.visible) {
                            switch (field.type) {
                                case CustomFieldType.checkbox:
                                    this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.checkbox]++;
                                    break;
                                case CustomFieldType.dropdown:
                                    this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.dropdown]++;
                                    break;
                                case CustomFieldType.textarea:
                                    this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.textarea]++;
                                    break;
                                default:
                                    this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.text]++;
                                    break;
                            }
                        }
                        return {
                            ...result,
                            ...this.customFieldsTypeToTrack,
                            ...this.customFieldsAddedTypeToTrack
                        };
                    }
                }, {})
            }
        );
    };

    private setToDefaultCustomFieldsTypeToTrack = () => {
        this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.text] = 0;
        this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.checkbox] = 0;
        this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.dropdown] = 0;
        this.customFieldsTypeToTrack['Custom Field - ' + CustomFieldType.textarea] = 0;
        this.customFieldsAddedTypeToTrack['Custom Field Added - ' + CustomFieldType.text] = convertBoolTypeForMixPanel(false);
        this.customFieldsAddedTypeToTrack['Custom Field Added - ' + CustomFieldType.checkbox] = convertBoolTypeForMixPanel(false);
        this.customFieldsAddedTypeToTrack['Custom Field Added - ' + CustomFieldType.dropdown] = convertBoolTypeForMixPanel(false);
        this.customFieldsAddedTypeToTrack['Custom Field Added - ' + CustomFieldType.textarea] = convertBoolTypeForMixPanel(false);
    };

  @mobx.action
    public openRegistrationFormPreview = (): void => {
      const link = getFormattedUrl(this.subdomain) + 'registration/';
      this.trackingService.get().track(TRACKING_EVENTS.CONFIGURE_REGISTRATION_FORM_PREVIEW, {});
      window.open(link, LinkTargetValue.BLANK);
    };

  @mobx.action
  private deleteCustomField = async (customFieldId: number, callback: (customFieldId: number) => void): Promise<void> => {
    mobx.runInAction(() => {
      this.isSaving = true;
    });
    try {
      await this.registrationConfigurationDAO.deleteCustomeField({
        accountId: this.accountId,
        eventId: this.eventId,
        customFieldId: customFieldId
      });
      callback(customFieldId);
    } catch (error) {
      FormRegistrationFormVM.handleApiException(error);
    } finally {
      mobx.runInAction(() => {
        this.isSaving = false;
      });
    }
  };
}
